import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

import Card from "../../components/shared/Card/Card";
import { usePageNameContext } from "../../contexts/PageTitleContext";

export default function VatMarginSchemeGuide() {
  const { setInfo } = usePageNameContext();

  useEffect(() => {
    setInfo({
      name: "VAT Margin Scheme",
      desc: "A guide for yard and office staff",
    });
  }, [setInfo]);

  return (
    <div className="mt-4 space-y-4">
      <Card>
        <div className="border-b border-slate-200 px-6 py-4">
          <Link
            to="/help"
            className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            Back to Help
          </Link>
        </div>
        <article className="prose prose-slate max-w-none px-6 py-8">
          <h1>Using the VAT Margin Scheme in Wilma</h1>
          <p className="lead">A guide for yard and office staff</p>

          <h2>What is the margin scheme?</h2>
          <p>
            When you buy a vehicle from a <strong>private seller</strong> (or anyone who
            doesn&apos;t charge you VAT), HMRC&apos;s <strong>Global Accounting Scheme</strong>{" "}
            (a version of the VAT margin scheme for second-hand goods) lets you pay VAT on your{" "}
            <strong>profit margin</strong> instead of on the full selling price of every part.
          </p>
          <p>In practice that means:</p>
          <ul>
            <li>
              You <strong>don&apos;t add VAT</strong> to the price of parts sold from those
              vehicles.
            </li>
            <li>
              Instead, at the end of each VAT period you pay VAT on the difference between what
              those parts sold for and what the vehicles cost you (the &quot;margin&quot;).
            </li>
            <li>
              Parts from vehicles where you <strong>were</strong> charged VAT (bought from a
              VAT-registered trade seller) still sell with <strong>standard VAT</strong> as
              normal.
            </li>
          </ul>
          <p>
            Wilma tracks all of this for you automatically. Your job is simply to record, for
            every vehicle, <strong>whether you were charged VAT when you bought it</strong>.
            Everything else follows from that one answer.
          </p>
          <blockquote>
            <p>
              <strong>Note:</strong> Wilma calculates the figures, but whether the scheme
              applies to your business is between you and your accountant. If you&apos;re unsure,
              check with them before turning it on.
            </p>
          </blockquote>

          <h2>Turning the scheme on</h2>
          <ol>
            <li>
              Go to <strong>Admin → VAT / Margin Scheme</strong>.
            </li>
            <li>
              Turn on <strong>Global Margin Scheme</strong>.
            </li>
            <li>
              Check the <strong>Standard VAT rate</strong> is correct (normally 20).
            </li>
            <li>Save.</li>
          </ol>
          <p>
            If the scheme is switched <strong>off</strong>, Wilma charges standard VAT on
            everything, exactly as before — none of the rest of this guide applies.
          </p>

          <h2>Step 1 — Record VAT status when you buy a vehicle</h2>
          <p>
            This is the only thing staff need to remember. For every vehicle, tell Wilma whether
            the seller charged you VAT:
          </p>
          <ul>
            <li>
              <strong>Bought from a private individual</strong> (scrap car from the public, most
              ELVs) → VAT was <strong>not</strong> charged. These vehicles go into the margin
              scheme.
            </li>
            <li>
              <strong>Bought from a VAT-registered business</strong> (trade seller, auction with
              VAT, fleet disposal) → VAT <strong>was</strong> charged. These vehicles stay on
              standard VAT.
            </li>
          </ul>
          <p>Where to set it:</p>
          <ul>
            <li>
              <strong>On the lead / quote</strong> — tick or untick{" "}
              <strong>VAT charged on purchase</strong> when creating or editing the lead. This is
              the normal route if you use Wilma&apos;s leads module. The setting carries through
              to the vehicle automatically.
            </li>
            <li>
              <strong>On the vehicle itself</strong> — open the vehicle and use the{" "}
              <strong>VAT on purchase</strong> control (or the{" "}
              <strong>VAT charged on purchase (trade seller)</strong> checkbox on the vehicle
              edit page). Use this if you don&apos;t use leads, or to correct a mistake. Only
              admins can change it.
            </li>
          </ul>
          <p>
            If a vehicle has more than one lead, Wilma keeps them all in step — changing the VAT
            answer on one updates the vehicle and the others, so they can never disagree.
          </p>

          <h3>What Wilma does with your answer</h3>
          <p>When the vehicle is collected (or when you set the flag directly):</p>
          <ul>
            <li>
              <strong>No VAT charged</strong> → the vehicle is marked as{" "}
              <strong>margin scheme</strong> stock, and its purchase cost is added to your{" "}
              <strong>margin pool</strong> (the running total of eligible purchase costs used
              for your VAT return).
            </li>
            <li>
              <strong>VAT charged</strong> → the vehicle is marked as{" "}
              <strong>standard VAT</strong> stock. Nothing goes in the pool.
            </li>
            <li>
              The <strong>self-billed purchase invoice</strong> Wilma generates on collection
              uses the right tax rate automatically (standard rate if VAT was charged, zero if
              not).
            </li>
          </ul>

          <h2>Step 2 — Sell parts as normal</h2>
          <p>
            You don&apos;t need to do anything different at the point of sale. Every part knows
            which vehicle it came from, so Wilma works out the right VAT treatment automatically
            — at the counter, on eBay, Shopify and PartPro:
          </p>
          <ul>
            <li>
              Parts from <strong>margin scheme</strong> vehicles sell with{" "}
              <strong>no VAT added</strong>. The invoice shows no separate VAT amount, is titled
              &quot;INVOICE&quot; rather than &quot;TAX INVOICE&quot;, and carries a note
              explaining the sale falls under the VAT Global Accounting Scheme (HMRC requires
              this wording).
            </li>
            <li>
              Parts from <strong>standard VAT</strong> vehicles sell with VAT added as normal,
              on a standard tax invoice.
            </li>
          </ul>

          <h3>Where you can see the status</h3>
          <ul>
            <li>
              <strong>Stock page</strong> — each part shows a pill: green{" "}
              <strong>Standard VAT 20%</strong> or amber <strong>Margin scheme</strong>, with
              &quot;(from vehicle)&quot; when it&apos;s inherited rather than overridden.
            </li>
            <li>
              <strong>Search</strong> — the search window shows the same{" "}
              <strong>Standard VAT</strong> / <strong>Margin scheme</strong> pill against stock
              results, plus <strong>VAT</strong> / <strong>GLOBAL</strong> on sold items (see
              eBay note below).
            </li>
            <li>
              <strong>Invoices</strong> — the invoice list and invoice page show a pill: green{" "}
              <strong>VAT</strong>, amber <strong>MARGIN</strong>, or blue{" "}
              <strong>GLOBAL</strong>. On the invoice&apos;s item list, margin lines are marked{" "}
              <strong>Margin</strong> and their tax rate is locked at 0% — Wilma won&apos;t let
              VAT be added to a margin line, even by hand.
            </li>
          </ul>

          <h3>High-value parts (£500 or more by default)</h3>
          <p>
            HMRC excludes individual high-value goods from Global Accounting. In{" "}
            <strong>Admin → VAT / Margin Scheme</strong> you can turn on{" "}
            <strong>Auto-apply standard VAT on high-value parts</strong> (on by default) and set
            the <strong>high-value threshold</strong> (default <strong>£500</strong>). When that
            is enabled, an order for a part at or above the threshold (eBay, PartPro, Shopify, or
            the counter basket) <strong>automatically applies standard VAT</strong> on the
            invoice — even if the donor vehicle is margin-scheme stock.
          </p>

          <h3>Overriding a single part</h3>
          <p>
            Occasionally a specific part shouldn&apos;t follow its vehicle even below £500. On
            the stock page, use <strong>Sales VAT treatment override</strong>:
          </p>
          <ul>
            <li>
              <strong>Inherit from vehicle</strong> (the default) — follow the vehicle&apos;s
              status.
            </li>
            <li>
              <strong>Force standard VAT</strong> — charge VAT on this part regardless of the
              vehicle.
            </li>
            <li>
              <strong>Force margin scheme</strong> — treat this part as margin stock regardless
              of the vehicle (still overridden automatically if the sale price is £500+).
            </li>
          </ul>

          <h3>A note on eBay &quot;GLOBAL&quot; sales</h3>
          <p>
            Sales fulfilled through eBay&apos;s <strong>Global Shipping Programme</strong> show a
            blue <strong>GLOBAL</strong> pill. That is a different thing entirely: eBay collects
            and pays the VAT on those orders, so Wilma charges no UK VAT on them. Don&apos;t
            confuse GLOBAL (eBay shipping) with the margin scheme (amber{" "}
            <strong>MARGIN</strong>).
          </p>

          <h2>Step 3 — Your VAT return</h2>
          <p>
            When it&apos;s time to do a VAT return, go to{" "}
            <strong>Admin → VAT / Margin Scheme → Period margin report</strong> and enter your
            VAT period dates. Wilma shows:
          </p>
          <ul>
            <li>
              <strong>Eligible purchases</strong> — margin pool at the start of the period, plus
              vehicles added during it, minus any removed.
            </li>
            <li>
              <strong>Eligible sales</strong> — everything sold under the margin scheme in the
              period (credit notes are deducted automatically).
            </li>
            <li>
              <strong>Margin</strong> — sales minus purchases.
            </li>
            <li>
              <strong>VAT due</strong> — the VAT fraction of the margin (at 20% that&apos;s
              1/6th). If the margin is negative (you bought more than you sold), no VAT is due
              on the scheme for that period, and the unused purchase costs stay in the pool and
              count towards future periods automatically.
            </li>
          </ul>
          <p>
            Give these figures to your accountant or use them directly in your return.
            Standard-rated sales appear in your normal VAT figures as usual — the report only
            covers margin stock.
          </p>

          <h2>Leaving the scheme</h2>
          <p>
            If you stop using the margin scheme, turn it off in{" "}
            <strong>Admin → VAT / Margin Scheme</strong>. Wilma will offer to{" "}
            <strong>close the pool</strong>: remaining margin vehicles are switched back to
            standard VAT and their pool entries are closed off. From then on everything sells
            with standard VAT again.
          </p>

          <h2>Quick reference</h2>
          <table>
            <thead>
              <tr>
                <th>I want to…</th>
                <th>Where</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Turn the scheme on/off, set the VAT rate</td>
                <td>Admin → VAT / Margin Scheme</td>
              </tr>
              <tr>
                <td>Say whether VAT was charged on a vehicle</td>
                <td>Lead payment details, or the vehicle page <strong>VAT on purchase</strong></td>
              </tr>
              <tr>
                <td>Check a part&apos;s VAT treatment</td>
                <td>Stock page pill, or search</td>
              </tr>
              <tr>
                <td>Force a part to standard VAT (e.g. £500+ component)</td>
                <td>Stock page → Sales VAT treatment override</td>
              </tr>
              <tr>
                <td>See how a sale was treated</td>
                <td>
                  Invoice pill: <strong>VAT</strong> / <strong>MARGIN</strong> /{" "}
                  <strong>GLOBAL</strong>
                </td>
              </tr>
              <tr>
                <td>Get figures for the VAT return</td>
                <td>Admin → VAT / Margin Scheme → Period margin report</td>
              </tr>
            </tbody>
          </table>

          <h2>Golden rules</h2>
          <ol>
            <li>
              <strong>Always answer &quot;was VAT charged?&quot; when buying a vehicle.</strong>{" "}
              It&apos;s the one thing Wilma can&apos;t work out for itself.
            </li>
            <li>
              <strong>Private seller = no VAT = margin scheme. Trade seller = VAT = standard.</strong>
            </li>
            <li>
              <strong>Never add VAT to a margin sale manually.</strong> Wilma prevents it, but
              don&apos;t try to work around it — a margin invoice must not show a separate VAT
              amount.
            </li>
            <li>
              <strong>£500+ sales get standard VAT automatically</strong> on invoices. You can
              still force standard on cheaper parts via the stock override if needed.
            </li>
            <li>
              <strong>When in doubt, ask your accountant</strong> — Wilma does the arithmetic,
              not the tax advice.
            </li>
          </ol>
        </article>
      </Card>
    </div>
  );
}
